import React, { forwardRef, memo } from 'react'
import styled, { css } from 'styled-components'
import { Spacings, Text, Devices } from '@styles'
import { FormElementContainer, Label, ErrorMessage, baseInputStyles } from '../styled'

const InputWrapper = styled.div`
	display: flex;
	align-items: stretch;
	width: 100%;
`

const IconBox = styled.div`
	display: flex;
	align-items: center;
	justify-content: center;
	padding: 0 ${Spacings.SPACING_3B};
	background: ${({ theme }) => theme.colors.surface.sunken};
	color: ${({ theme }) => theme.colors.text.secondary};
	font-size: ${Text.SMALL};
	font-weight: ${Text.BOLD_500};
	white-space: nowrap;
	border: 1px solid ${({ theme, $hasError }) => ($hasError ? theme.colors.status.error.border : theme.colors.border.default)};

	${({ $position, theme }) => $position === 'left' && css`
		border-right: none;
		border-radius: ${theme.radius.sm} 0 0 ${theme.radius.sm};
	`}

	${({ $position, theme }) => $position === 'right' && css`
		border-left: none;
		border-radius: 0 ${theme.radius.sm} ${theme.radius.sm} 0;
	`}

	@media ${Devices.tablet} {
		font-size: ${Text.MEDIUM};
	}
`

const StyledInput = styled.input`
	${baseInputStyles}

	${({ $hasLeftIcon }) => $hasLeftIcon && css`
		border-left: none;
		border-top-left-radius: 0;
		border-bottom-left-radius: 0;
	`}

	${({ $hasRightIcon }) => $hasRightIcon && css`
		border-right: none;
		border-top-right-radius: 0;
		border-bottom-right-radius: 0;
	`}
`

const Input = forwardRef(({
	id,
	label,
	error,
	disabled,
	className,
	placeholderFontSize,
	leftIcon,
	rightIcon,
	...props
}, ref) => {
	const hasError = !!error
	const hasLeftIcon = !!leftIcon
	const hasRightIcon = !!rightIcon

	// A focused number input changes its value on wheel-scroll, so scrolling the page over one
	// silently edits the amount. Blur it on wheel so the scroll never reaches the input.
	const wheelHandler = (event) => {
		if (props.type === 'number') event.currentTarget.blur()
		props.onWheel?.(event)
	}

	return (
		<FormElementContainer className={className}>
			{label && (
				<Label htmlFor={id} disabled={disabled}>
					{label}
				</Label>
			)}
			<InputWrapper>
				{hasLeftIcon && (
					<IconBox $position='left' $hasError={hasError}>
						{leftIcon}
					</IconBox>
				)}
				<StyledInput
					ref={ref}
					id={id}
					disabled={disabled}
					$hasError={hasError}
					$placeholderFontSize={placeholderFontSize}
					$hasLeftIcon={hasLeftIcon}
					$hasRightIcon={hasRightIcon}
					autoComplete='off'
					{...props}
					onWheel={wheelHandler}
				/>
				{hasRightIcon && (
					<IconBox $position='right' $hasError={hasError}>
						{rightIcon}
					</IconBox>
				)}
			</InputWrapper>
			{error && <ErrorMessage>{error}</ErrorMessage>}
		</FormElementContainer>
	)
})

Input.displayName = 'Input'

export default memo(Input)
